'use client';
import { useCallback, useRef, useImperativeHandle, RefObject } from 'react';
import ReactCanvasConfetti from 'react-canvas-confetti';
import { CreateTypes } from 'canvas-confetti';

export type ConfettiRefProps = {
  fire: (originX: number, originY: number) => void;
} | null;

export const Confetti = ({
  ref,
}: {
  props?: ConfettiRefProps;
  ref: RefObject<ConfettiRefProps | null>;
}) => {
  const refAnimationInstance = useRef<CreateTypes | null>(null);

  const getInstance = useCallback((instance: CreateTypes | null) => {
    refAnimationInstance.current = instance;
  }, []);

  const makeShot = useCallback(
    (particleRatio: number, opts: object, originX: number, originY: number) => {
      refAnimationInstance.current?.({
        ...opts,
        origin: { x: originX, y: originY },
        particleCount: Math.floor(100 * particleRatio), // Reduced particle count
      });
    },
    [],
  );

  const fire = useCallback(
    (originX: number, originY: number) => {
      makeShot(
        0.25,
        {
          spread: 26,
          startVelocity: 55,
        },
        originX,
        originY,
      );

      makeShot(
        0.2,
        {
          spread: 60,
        },
        originX,
        originY,
      );

      makeShot(
        0.35,
        {
          spread: 100,
          decay: 0.91,
          scalar: 0.8,
        },
        originX,
        originY,
      );

      makeShot(
        0.1,
        {
          spread: 120,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
        },
        originX,
        originY,
      );

      makeShot(
        0.1,
        {
          spread: 120,
          startVelocity: 45,
        },
        originX,
        originY,
      );
    },
    [makeShot],
  );

  useImperativeHandle(ref, () => ({
    fire,
  }));

  return (
    <ReactCanvasConfetti
      refConfetti={getInstance}
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 1000,
      }}
    />
  );
};
